import { PREVIEW_NS, type PreviewDemo, type PreviewNavigatedMessage, type PreviewNavigateMessage } from "@innoclouds/ui";
import type { ISizes } from "../types/iCardProduct";
import { find, first, inView, isOpen, sleep, tap, waitFor } from "./tiles/dom";
import { dismissStart, openProduct } from "./tiles/menu";
import { ABSENT, NAVIGABLE } from "./tiles/places";

const EMPTY_DEMO: PreviewDemo = { subjects: [], upsell: "", combo: "" };

// Оболочка присылает переход сразу после перезапуска приложения, а меню точки грузится дольше обычного ожидания
const APP_START = 20000;

// Карточка блюда открывается сразу, а содержимое ждёт данных блюда с бэкенда — бывает дольше ожидания сценария
const CONTENT_WAIT = 25000;

// Предложения рекомендаций и комбо, уведомления появляются не сразу после действия
const POPUP_WAIT = 1200;

// Сколько раз подряд закрывать окна — они могут всплывать одно за другим
const CLEAR_STEPS = 6;

// Сколько карточек попробовать, если вместо блюда открылось комбо
const CARD_ATTEMPTS = 3;

// Карточка открылась, а разметки нет столько времени — значит, в этом дизайне она не размечена
const UNMARKED_GRACE = 8000;

const UNMARKED = "unmarked";

const normalize = (text: string) => text.replace(/\s+/g, " ").trim().toLowerCase();

const shown = (selector: string) =>
	[...document.querySelectorAll<HTMLElement>(selector)].find((el) => {
		const rect = el.getBoundingClientRect();
		return rect.width > 0 && rect.height > 0;
	}) ?? null;

interface Blocker {
	selector: string;
	dismiss: (el: HTMLElement) => void;
}

/** Окна, которые встают на пути сценария: закрываем по очереди, пока экран не очистится */
const BLOCKERS: Blocker[] = [
	{ selector: "[data-sonner-blocker]", dismiss: (el) => el.click() },
	{ selector: ".ordertype-modal", dismiss: (el) => el.querySelector<HTMLElement>(".ordertype-modal-variant")?.click() },
	{ selector: ".loyalty_promo_overlay", dismiss: (el) => el.click() },
	{
		selector: ".modal_loyal_overlay",
		dismiss: (el) => (el.querySelector<HTMLElement>(".modal_loyal_close") ?? el).click(),
	},
	{ selector: ".recommendation_wrapper", dismiss: (el) => el.querySelector<HTMLElement>(".header_close_recommendation")?.click() },
	{ selector: ".popup_overlay_combo", dismiss: (el) => el.querySelector<HTMLElement>(".header_close_combos")?.click() },
	{ selector: ".header_close_combo", dismiss: (el) => el.click() },
];

/** keep — окна, которые сами являются целью перехода (рекомендации, комбо) */
const clearWay = async (keep: string[] = []) => {
	for (let step = 0; step < CLEAR_STEPS; step++) {
		const blocker = BLOCKERS.find(({ selector }) => !keep.includes(selector) && shown(selector));
		const el = blocker && shown(blocker.selector);
		if (!blocker || !el) return;
		blocker.dismiss(el);
		await sleep(600);
	}
};

const titleOf = (card: HTMLElement) => normalize(card.querySelector<HTMLElement>('[data-el="card_title"]')?.innerText ?? "");

/** Карточки настоящих блюд (без комбо и папок-групп) — сначала видимые, потом те, до которых надо докрутить */
const productCards = () =>
	find("main", "card")
		.filter((card) => !card.querySelector('[data-el="combo_card_body"], .card__main_folder'))
		.sort((a, b) => Number(inView(b)) - Number(inView(a)));

const cardsFrom = (names: string[] | undefined) => {
	const wanted = new Set((names ?? []).map(normalize));
	return productCards().filter((card) => wanted.has(titleOf(card)));
};

const reveal = async (card: HTMLElement) => {
	if (inView(card)) return;
	card.scrollIntoView({ block: "center" });
	await sleep(400);
};

const sheetHasContent = () => (document.querySelector('[class*="product_card_sheet"]')?.children.length ?? 0) > 0;

/** Открыть карточку блюда: сначала блюда с добавками и размерами, на них видно больше элементов */
const openProductCard = async (demo: PreviewDemo): Promise<true | typeof UNMARKED | null> => {
	const cards = [...new Set([...cardsFrom(demo.rich), ...cardsFrom(demo.plain), ...productCards()])];
	for (const card of cards.slice(0, CARD_ATTEMPTS)) {
		await reveal(card);
		card.querySelector<HTMLElement>('[data-el="card_inner"]')?.click();
		let sheetSince = 0;
		const outcome = await waitFor(() => {
			if (isOpen("product_card")) return "card";
			if (shown(".popup_overlay_combo")) return "combo";
			if (sheetHasContent()) {
				sheetSince ||= Date.now();
				if (Date.now() - sheetSince > UNMARKED_GRACE) return UNMARKED;
			}
			return null;
		}, CONTENT_WAIT);
		if (outcome === "card") return true;
		if (outcome === UNMARKED) return UNMARKED;
		await clearWay();
	}
	return null;
};

/**
 * Положить в корзину простое блюдо кнопкой «+» на карточке меню — без карточки блюда и выбора добавок,
 * так работает при любом дизайне модификаторов. Блюда с обязательными добавками и комбо не берём:
 * без выбора их в корзину не положить, а карточка блюда размечена не во всех дизайнах.
 */
const fillCart = async (demo: PreviewDemo) => {
	const card = cardsFrom(demo.plain)[0];
	if (card) {
		await reveal(card);
		card.querySelector<HTMLElement>('[data-el="card_add_button"]')?.click();
		await sleep(POPUP_WAIT);
		await clearWay();
		return true;
	}
	// Полностью простых блюд нет — кладём блюдо без обязательного выбора напрямую, собрав позицию так же,
	// как карточка блюда: с размером по умолчанию и без добавок
	const product = demo.cartProduct as { price?: number | null; sizes?: ISizes[] } | undefined;
	if (!product) return false;
	const [{ useMainStore }, { buildCartProductFromCardState }] = await Promise.all([import("../store/main"), import("../utils/buildCartProduct")]);
	const size = product.sizes?.find((item) => item.is_default) ?? product.sizes?.[0] ?? null;
	const item = buildCartProductFromCardState({
		openCard: { ...product, amount: 1, price: size?.price ?? product.price ?? 0 } as never,
		selectedSize: size,
		groupModifiersSelected: [],
		groupModifiers: [],
	});
	useMainStore().addToCart(item);
	await sleep(POPUP_WAIT);
	await clearWay();
	return true;
};

export const navigate = async ({ id, place, product, demo }: PreviewNavigateMessage): Promise<PreviewNavigatedMessage> => {
	const reply = (status: PreviewNavigatedMessage["status"], message?: string): PreviewNavigatedMessage => ({
		ns: PREVIEW_NS,
		type: "navigated",
		id,
		status,
		place,
		...(message ? { message } : {}),
	});
	const target = NAVIGABLE[place];
	if (!target) return reply("error", `Нет такого места. Есть: ${Object.keys(NAVIGABLE).join(", ")}`);

	const base = demo ?? EMPTY_DEMO;
	const scenario = product ? { ...base, subjects: [product, ...base.subjects.filter((subject) => subject !== product)] } : base;
	// Цель перехода сама может быть окном — его не закрываем
	const keep = place === "recommendations" ? [".recommendation_wrapper"] : place === "combo" ? [".popup_overlay_combo", ".header_close_combo"] : [];
	const notes: string[] = [];
	try {
		await waitFor(() => document.querySelector(".start_page") ?? (isOpen("main") ? document.body : null), APP_START);
		await dismissStart();
		if (shown(".page-not-working")) return reply("error", "Точка сейчас не работает по расписанию — превью показывает заглушку, перейти никуда нельзя");
		await clearWay();

		let entered: unknown;
		if (place === "product_card") {
			entered = product ? await openProduct(product, scenario) : null;
			if (!entered) {
				if (product) notes.push(`Блюдо «${product}» не нашлось в меню — открыто другое блюдо`);
				await clearWay();
				entered = await openProductCard(base);
			}
		} else if (place === "cart") {
			const filled = await fillCart(base);
			tap(await waitFor(() => first("main", "cart_button")));
			entered = await waitFor(() => (isOpen("cart") ? true : null));
			if (entered && !find("cart", "line").length)
				notes.push(filled ? "Корзина пустая: блюдо не добавилось" : "Корзина пустая: в меню не нашлось блюда, которое кладётся без выбора добавок");
		} else entered = await target.enter(scenario);

		if (entered === ABSENT) return reply("missing", `Не получилось открыть «${target.title}»`);
		if (entered === UNMARKED)
			return reply("missing", "Карточка блюда открылась, но в этом дизайне она не размечена — нужен «Новый» дизайн модификаторов в киоске");
		if (!entered && !(await waitFor(() => (target.scopes().length ? true : null), CONTENT_WAIT)))
			return reply("missing", `Не получилось открыть «${target.title}»`);
		await target.seek?.(scenario);
		await sleep(POPUP_WAIT);
		await clearWay(keep);
		if (!target.scopes().length) return reply("missing", `«${target.title}» открылось, но размеченных элементов там нет`);
		return reply("ok", notes.join(". ") || undefined);
	} catch (error) {
		return reply("error", error instanceof Error ? error.message : String(error));
	}
};
